import React, { memo, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

import { Alert, Spin } from 'antd';

import styled from 'styled-components';
import fontService, { FontWeight, TextType } from 'services/FontService';
import { desktop, mobile, tablet } from 'services/ui';
import { Colors } from 'context';
import { Button } from 'atoms';
import { CareerLessonRow, CareerProgress } from 'molecules';
import { useCareerLessons } from 'core';
import { PATHS } from 'router/config';

import { useCareerAccessGuard } from './useCareerAccessGuard';

/**
 * /career/learning — the ordered career-lesson list (spec 0018 §6).
 *
 * Every row's state comes straight from the server: completed / open / locked
 * is `isCompleted` + `isUnlocked`, never a client-side date or index rule.
 */
export const CareerLearning = memo(() => {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const { center } = useCareerAccessGuard();
  const { lessons, loading, error } = useCareerLessons();

  // Закладки. Раньше единственным их следом была звёздочка у названия, и
  // «куда делись мои закладки» был честный вопрос — отобрать их было негде.
  const [onlyBookmarks, setOnlyBookmarks] = useState(false);
  const bookmarkedCount = useMemo(
    () => lessons.filter((lesson) => lesson.isBookmarked).length,
    [lessons],
  );
  // Фильтр считается включённым, только пока закладки есть. Иначе снятие
  // последней закладки (здесь или на самом уроке) оставило бы включённый
  // фильтр над пустым списком, и раздел выглядел бы сломанным.
  const filterOn = onlyBookmarks && bookmarkedCount > 0;
  const visibleLessons = useMemo(
    () => (filterOn ? lessons.filter((lesson) => lesson.isBookmarked) : lessons),
    [filterOn, lessons],
  );

  // «Продолжить» → the first unlocked-but-unfinished lesson. Computed purely
  // from the server-provided flags (never a client-side unlock rule); when every
  // unlocked lesson is done there is nothing to continue and the CTA hides.
  const nextLesson = useMemo(
    () => lessons.find((lesson) => lesson.isUnlocked && !lesson.isCompleted) ?? null,
    [lessons],
  );

  return (
    <PageWrapper>
      <BackLink type="button" onClick={() => navigate(PATHS.careerCenter)}>
        ← {t('careerCenter.learning.back')}
      </BackLink>

      <PageHeader>
        <PageTitle>{t('careerCenter.learning.title')}</PageTitle>
        <PageInfo>{t('careerCenter.learning.info')}</PageInfo>
      </PageHeader>

      {center ? (
        <ProgressCard>
          <CareerProgress
            completed={center.lessonsCompleted}
            total={center.lessonsTotal}
            percent={center.progressPercent}
          />
        </ProgressCard>
      ) : null}

      {nextLesson ? (
        <ContinueBar>
          <ContinueButton onClick={() => navigate(`${PATHS.careerLearning}/${nextLesson.id}`)}>
            {t('careerCenter.learning.continue')}
          </ContinueButton>
        </ContinueBar>
      ) : null}

      {bookmarkedCount ? (
        <FilterBar>
          <FilterChip type="button" isActive={!filterOn} onClick={() => setOnlyBookmarks(false)}>
            {t('careerCenter.learning.filterAll')}
          </FilterChip>
          <FilterChip type="button" isActive={filterOn} onClick={() => setOnlyBookmarks(true)}>
            ★ {t('careerCenter.learning.filterBookmarked', { n: bookmarkedCount })}
          </FilterChip>
        </FilterBar>
      ) : null}

      {error ? (
        <Alert type="error" showIcon message={t('careerCenter.learning.loadError')} />
      ) : loading && !lessons.length ? (
        <SpinWrapper>
          <Spin size="large" />
        </SpinWrapper>
      ) : visibleLessons.length ? (
        <LessonsList>
          {visibleLessons.map((lesson) => (
            <CareerLessonRow
              key={lesson.id}
              order={lesson.order}
              title={lesson.title}
              summary={lesson.summary}
              durationMin={lesson.durationMin}
              isUnlocked={lesson.isUnlocked}
              isCompleted={lesson.isCompleted}
              isOpened={lesson.isOpened}
              isBookmarked={lesson.isBookmarked}
              onOpen={() => navigate(`${PATHS.careerLearning}/${lesson.id}`)}
            />
          ))}
        </LessonsList>
      ) : (
        <EmptyState>{t('careerCenter.learning.empty')}</EmptyState>
      )}
    </PageWrapper>
  );
});

const PageWrapper = styled.div`
  width: 100%;
  margin-bottom: 80px;

  ${desktop} {
    margin-bottom: 104px;
  }
`;

const BackLink = styled.button`
  margin-top: 32px;
  padding: 0;
  border: none;
  background: none;
  cursor: pointer;
  color: ${Colors.gray_dark};
  ${fontService.text({ type: TextType.small, weight: FontWeight.medium })}

  &:hover {
    color: ${Colors.main};
  }
`;

const PageHeader = styled.div`
  margin: 16px 0 32px 0;
`;

const FilterBar = styled.div`
  display: flex;
  gap: 8px;
  margin-bottom: 16px;
  flex-wrap: wrap;
`;

const FilterChip = styled.button<{ isActive: boolean }>`
  padding: 6px 14px;
  border-radius: 16px;
  cursor: pointer;
  border: 1px solid ${({ isActive }) => (isActive ? Colors.main : Colors.gray)};
  background: ${({ isActive }) => (isActive ? Colors.main : 'transparent')};
  // btn_text, а не black: в тёмной теме Colors.black это #ECECEC, и активная
  // «жёлтая» кнопка получала светлый текст на жёлтом фоне — нечитаемо.
  color: ${({ isActive }) => (isActive ? Colors.btn_text : Colors.gray_dark)};
  ${fontService.text({ type: TextType.small, weight: FontWeight.medium })}

  &:hover {
    border-color: ${Colors.main};
  }
`;

const PageTitle = styled.h2`
  margin: 0 0 4px 0;
  ${fontService.text({ size: 18, lineHeight: 133, weight: FontWeight.bold })}

  ${tablet} {
    margin: 0 0 8px 0;
    ${fontService.h3()}
  }

  ${desktop} {
    ${fontService.h2()}
  }
`;

const PageInfo = styled.p`
  margin: 0;
  ${fontService.text({ type: TextType.small, weight: FontWeight.regular })}
`;

const ProgressCard = styled.div`
  box-sizing: border-box;
  width: 100%;
  padding: 24px;
  margin-bottom: 32px;
  border: 1px solid ${Colors.system_gray};
  border-radius: 16px;
  background-color: ${Colors.white};
`;

const ContinueBar = styled.div`
  width: 100%;
  margin-bottom: 24px;
`;

const ContinueButton = styled(Button)`
  width: auto;

  ${mobile} {
    width: 100%;
  }
`;

const LessonsList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
  width: 100%;
`;

const SpinWrapper = styled.div`
  width: 100%;
  display: flex;
  justify-content: center;
  padding: 48px 0;
`;

const EmptyState = styled.div`
  width: 100%;
  padding: 48px 0;
  text-align: center;
  ${fontService.text({ size: 16, lineHeight: 133, weight: FontWeight.regular })}
  color: ${Colors.gray_dark};
`;
